import { Toggle, Translation } from '@ps/web-shared-components';
import { required } from '@ps/web-shared-validators';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useSearchParams from '@swf/core/hooks/useSearchParams';
import { select } from '@swf/core/store';
import { encodeQuery } from '@swf/core/utils';
import { CrossOperatorDepositLimitForm } from '@swf/notification';

import AccountInfoMessage from '../AccountInfoMessage/AccountInfoMessage';
import AddressFields from '../AddressForm/AddressFields/AddressFields';
import AddressFormLayout from '../AddressForm/AddressFormLayout/AddressFormLayout';
import StreetAddressExtended from '../AddressForm/StreetAddressExtended/StreetAddressExtended';
import type { AddressFormValues } from '../AddressForm/useAddressUpdate';

import {
  TXTCLI_A_SCHUFA_check_will_be_perform_ELL,
  TXTCLI_German_regulations_require_us__ELL,
  TXTCLI_I_understand_that_my_account_m_ELL,
  TXTCLI_You_have_1_day_to_check_that_y_ELL_1,
  TXTCLI_You_have_N0_days_to_check_that_ELL_1
} from './DeAnnualAttestationAddressForm.messages.json';
import DeAnnualAttestationAddressFullName from './DeAnnualAttestationAddressFullName/DeAnnualAttestationAddressFullName';
import useOnCompleteAttestation, {
  DeAddressFormType,
  agreementId
} from './useOnCompleteAttestation';

interface Props {
  disabled?: boolean;
  className?: string;
  setMessageNode?: (node: React.ReactNode) => void;
  setResult?: (result: 'success' | 'fail') => void;
  submitButtonBlock?: boolean;
  formType?: DeAddressFormType;
  callback?: (result: DeAddressFormType) => void;
}

const DeAnnualAttestationAddressForm = ({
  disabled,
  className,
  setMessageNode,
  setResult,
  submitButtonBlock = false,
  formType,
  callback
}: Props) => {
  const { docsDaysLeft = 30 } = useSelector(select.account.get);
  const fullName = useSelector(select.account.getFullName);
  const onCompleteAttestation = useOnCompleteAttestation({
    setMessageNode,
    setResult,
    callback
  });
  const searchParams = useSearchParams<object>();
  const { replace } = useHistory();
  const [limit, setLimit] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setMessageNode?.(
      <AccountInfoMessage type="warning">
        <span>
          <Translation {...TXTCLI_German_regulations_require_us__ELL} />{' '}
          <Translation
            {...(docsDaysLeft === 1
              ? TXTCLI_You_have_1_day_to_check_that_y_ELL_1
              : {
                  ...TXTCLI_You_have_N0_days_to_check_that_ELL_1,
                  values: { 0: docsDaysLeft }
                })}
          />{' '}
          <Translation {...TXTCLI_A_SCHUFA_check_will_be_perform_ELL} />
        </span>
      </AccountInfoMessage>
    );
  }, [docsDaysLeft, setMessageNode]);

  useEffect(() => {
    if (limit) {
      setLimit(false);
      onCompleteAttestation(searchParams as AddressFormValues)
        .then(() => {
          replace({ search: encodeQuery({}) });
          setResult?.('success');
        })
        .catch(() => {
          replace({ search: encodeQuery({}) });
          setResult?.('fail');
        })
        .finally(() => {
          setLoading(false);
          callback?.(DeAddressFormType.Address);
        });
    }
  }, [
    limit,
    setLimit,
    formType,
    onCompleteAttestation,
    searchParams,
    replace,
    setResult,
    callback
  ]);

  return formType === DeAddressFormType.Address ? (
    <AddressFormLayout
      onSubmit={onCompleteAttestation}
      submitButtonBlock={submitButtonBlock}
      className={className}
      optionalInitialData={{ fullName }}
    >
      {(form, submitButton) => (
        <>
          {form.submitError}
          <DeAnnualAttestationAddressFullName />
          <AddressFields disabled={disabled} showStates restricted={false}>
            <StreetAddressExtended disabled={disabled} />
          </AddressFields>
          <Toggle
            label={TXTCLI_I_understand_that_my_account_m_ELL}
            id={agreementId}
            type="checkbox"
            validators={[required()]}
          />
          {submitButton}
        </>
      )}
    </AddressFormLayout>
  ) : (
    <CrossOperatorDepositLimitForm
      setLimitSuccess={setLimit}
      loading={loading}
      setLoading={setLoading}
    />
  );
};

export default DeAnnualAttestationAddressForm;
